/** Where the server says a checkout stands. */
export type CheckoutStatus = 'paid' | 'unpaid' | 'processing' | 'expired'

/**
 * What a page coming back to a checkout knows about it.
 *
 * `checking` — the server has not answered yet. `error` — it could not be
 * asked. Neither says anything about money, so neither may tell the payer
 * nothing was charged, and neither may offer the payment again: the payment
 * may have gone through. `processing` is confirmed and still clearing, which
 * is paid as far as the payer is concerned. Only a loaded `unpaid` or
 * `expired` is a payment that did not happen.
 */
export type ReturnedCheckout =
  | 'none'
  | 'checking'
  | 'error'
  | 'paid'
  | 'processing'
  | 'unpaid'
  | 'expired'

export function returnedCheckout(input: {
  checkoutId: string | null
  status: CheckoutStatus | undefined
  isError: boolean
}): ReturnedCheckout {
  if (input.checkoutId === null) return 'none'
  if (input.isError) return 'error'
  return input.status ?? 'checking'
}

/**
 * The page offers no payment while it does not know the last one failed: a
 * second payment offered then is one the payer may make twice.
 */
export function holdsThePayment(state: ReturnedCheckout): boolean {
  return state === 'checking' || state === 'error' || state === 'processing'
}

export const RETURNED_CHECKOUT_NOTICE = {
  checking: 'Checking your payment…',
  error:
    'We could not check this payment just now. Reload the page in a moment to see whether it went through before paying again.',
  processing:
    'Your payment is processing. You will get an email once it is confirmed — there is nothing more to do.',
  unpaid: 'Your payment has not completed, and nothing was charged.',
  expired: 'This payment session expired and nothing was charged.',
} satisfies Record<Exclude<ReturnedCheckout, 'none' | 'paid'>, string>
